import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import {
  useDeleteForm,
  useDuplicateForm,
  useFormCategoryLookup,
  useFormsQuery,
} from "../api/queries";
import { FormsTour, useFormsTour } from "../tour/FormsTour";
import { FormsFilter } from "../ui/FormsFilter";
import { FormsTable } from "../ui/FormsTable";
import { FormsToolbar } from "../ui/FormsToolbar";

export function FormsListPage() {
  const nav = useNavigate({ from: "/app/forms" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/forms" });

  const list = useFormsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission([Permissions.forms.create]);
  const deleteForm = useDeleteForm();
  const duplicateForm = useDuplicateForm();
  const qc = useQueryClient();
  const tour = useFormsTour();

  // Fetch categories for filter using lookup endpoint
  const { data: categoriesData } = useFormCategoryLookup({ limit: 100 });
  const categories =
    categoriesData?.items?.map((c: { id: string; name: string }) => ({
      id: String(c.id),
      name: String(c.name),
    })) ?? [];

  const handleDelete = async (id: string) => {
    await deleteForm.run(id);
  };

  const handleChangeStatus = async (id: string, status: string) => {
    const { formsApi } = await import("../api/client");
    await formsApi.update(id, {
      status: status as "active" | "under_revision" | "discontinued" | "draft",
    });
    qc.invalidateQueries({ queryKey: ["forms"] });
  };

  return (
    <>
      <Header />

      <Main>
        <FormsTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <FormsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/forms/new" })}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <FormsFilter categories={categories} />

          <FormsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({
                search: {
                  offset,
                  limit,
                  sort: search.sort as
                    | Array<{ field: string; dir: "asc" | "desc" }>
                    | undefined,
                  filters: search.filters as
                    | Array<{ field: string; op: string; value: unknown }>
                    | undefined,
                },
              })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  offset: search.offset ?? 0,
                  limit: search.limit ?? 20,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                  filters:
                    (search.filters as
                      | Array<{ field: string; op: string; value: unknown }>
                      | undefined) ?? undefined,
                },
              })
            }
            onDetail={(form) =>
              nav({
                to: "/app/forms/$id",
                params: { id: form.id },
              })
            }
            onEdit={(form) =>
              nav({ to: "/app/forms/$id/edit", params: { id: form.id } })
            }
            onDelete={handleDelete}
            onChangeStatus={handleChangeStatus}
            onCopy={async (id) => {
              await duplicateForm.mutateAsync(id);
            }}
            onViewAnalytics={(id) => {
              nav({
                to: "/app/forms/$id/analysis",
                params: { id },
              });
            }}
            onViewSubmissions={(id) => {
              nav({
                to: "/app/forms/$id/submissions",
                params: { id },
                search: {
                  offset: 0,
                  limit: 20,
                  sort: undefined as
                    | Array<{ field: string; dir: "asc" | "desc" }>
                    | undefined,
                  filters: undefined as
                    | Array<{ field: string; op: string; value: unknown }>
                    | undefined,
                },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
